import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';
import { validateRoute } from '../../../api/v2/epo';
import dots from '../../../images/3dots.svg';
import { postEPOExport } from '../../apiCalls';
import { GoogleAnalyticsContext } from '../../GoogleAnalytics';
import { EPOFilterableSelect } from '../../Input/Input';
import { useStepper } from '../../Stepper';
import { StoreContext } from '../../Store';
import StepWrapper from './StepWrapper';

export function EpoDirectionPage({ DDAStatus }) {
  const { stepForward, localData, setDataByKey, activeStepKey, gotoStep, unvisitStep } = useStepper();

  const store = useContext(StoreContext);
  const queryClient = useQueryClient();

  const changeExCountry = async (data) => {
    setDataByKey('exportId', data.id);
    setDataByKey('exportCountry', data.abbreviation);
    setDataByKey('fromAreaCode', data.areaCode);
    const fd = new FormData();
    fd.append('from', localData.exportId);
    fd.append('to', 'true');
    fd.append('movementType', localData.movementType);

    const updateImportDropdown = await postEPOExport('routeChoice', fd);
    if (updateImportDropdown) {
      store.toFlagOptions[1](updateImportDropdown);
      updateImportDropdown.forEach((e) => {
        if (e.abbreviation === 'gb') {
          setDataByKey('importCountry', 'gb');
          setDataByKey('importId', e.id);
          setDataByKey('toAreaCode', e.areaCode);
        }
      });
    }

    postEPOExport('routeChoice', fd).then((data) => {
      store.toFlagOptions[1](data);
    });
  };

  const changeImCountry = (data) => {
    setDataByKey('toAreaCode', data.areaCode);
    setDataByKey('importId', data.id);
    setDataByKey('importCountry', data.abbreviation);
    setDataByKey('addReqNeither', false);
    setDataByKey('tad', false);
    setDataByKey('ens', false);
    setDataByKey('officeDest', '');
    setDataByKey('inlandTransId', '');
    setDataByKey('sealNo', '');
    unvisitStep('4. AdditionalRequirements');
    unvisitStep('3. Entry Port');
    unvisitStep('2. Exit Port');
  };

  const openRouteErrorModal = () => {
    localData.openRedirectmodal({
      title: 'Invalid route',
      body: (
        <>
          <p className="bold">Error - Not a supported route</p>
          <p>
            The movement type you have selected is not currently supported by CABIE. For the route you have selected,
            click{' '}
            <a target="_blank" rel="noreferrer" href="https://www.eori.uk/Customs_Entry">
              HERE
            </a>{' '}
            for data capture options.
          </p>
          <p>
            For more details please contact us below:
            <div className="bold text-eoriblue text-nowrap ms-2 mt-1">P: +44 (0) 333 012 4819</div>
            <div className="bold text-eoriblue ms-2">
              E:{' '}
              <a className="bold text-eoriblue" href="mailto:cabiesupport@eori.uk?subject=CABIE Issue">
                CABIEsupport@eori.uk
              </a>
            </div>
          </p>
        </>
      ),
    });
  };

  const nextPage = async () => {
    if (!localData.exportId || !localData.importId) {
      openRouteErrorModal();
      return;
    }

    const fd = new FormData();
    fd.append('from', localData.exportId);
    fd.append('to', localData.importId);

    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['validateRoute', localData.exportId, localData.importId],
        queryFn: () => validateRoute(fd),
        staleTime: Infinity,
      });

      if (!data[0].isValid) {
        throw new Error('invalid route');
      }

      if (data[0].type === 'Export') {
        setDataByKey('movementType', 'Export');
        localData.entryport = undefined;
        store.epoDirection[1](data[0].direction);
        gotoStep('2. Exit Port');
      }
      if (data[0].type === 'Import') {
        setDataByKey('movementType', 'Import');
        localData.exitport = undefined;
        store.epoDirection[1](data[0].direction);
        if (DDAStatus === 0) {
          // check not preferential origin country shortcode for Swiss and Turkey Norway Liechtenstein Iceland North Macedonia
          if (!['ch', 'tr', 'no', 'is', 'li', 'mk'].includes(localData.exportCountry)) {
            unvisitStep('Preferential Origin');
            setDataByKey('euPrefSelected', true);
            gotoStep('eu_origin');
          } else {
            setDataByKey('euPrefSelected', false);
            stepForward();
          }
        } else {
          setDataByKey('euPref', 'yes');
          setDataByKey('pref', 'yes');
          stepForward();
        }
      }
    } catch {
      openRouteErrorModal();
    }
  };

  const isInitialized = useRef(false);
  const ga = useContext(GoogleAnalyticsContext);

  useEffect(() => {
    if (ga.gaInitialized) {
      if (activeStepKey === '1. Direction' && isInitialized.current === false) {
        isInitialized.current = true;
        window?.gtag?.('event', '1. Direction', {
          event_category: 'EPO',
        });
      }
    }
  }, [activeStepKey, ga.gaInitialized]);

  return (
    <StepWrapper
      stepTitle="Type of Movement"
      stepText="Route"
      stepKey="1. Direction"
      handleNextClick={nextPage}
      isFirst
    >
      <div className="d-flex flex-row justify-content-center">
        <div className="step-banner d-flex mt-2 mb-2 py-4 flex-column justify-content-center flags align-items-center px-4">
          <img style={{ marginTop: '25px' }} className="p-4" src={`/flags/${localData.exportCountry}.svg`} alt="" />
          <div style={{ marginTop: '25px' }}>
            <EPOFilterableSelect
              label="Ship from"
              data={localData.exportId}
              setData={(data) => changeExCountry(data)}
              options={store.fromFlagOptions[0]}
              filter={1}
              className={'epo-dropdown'}
              required
              disabled={!localData.movementType}
              smallValue={true}
              dataTestId="shipFrom-dropdown"
            />
          </div>
        </div>
        <div className=" step-banner-link d-flex">
          <img src={dots} alt="" width="30" style={{ marginBottom: '85px' }} />
        </div>

        <div className="step-banner d-flex mt-2 mb-2 py-4 flex-column justify-content-center flags align-items-center px-4">
          <img style={{ marginTop: '25px' }} className="p-4 " src={`/flags/${localData.importCountry}.svg`} alt="" />
          <div style={{ marginTop: '25px' }}>
            <EPOFilterableSelect
              label="Ship to"
              name="importCountry"
              data={localData.importId}
              setData={(data) => changeImCountry(data)}
              options={store.toFlagOptions[0]}
              className={'epo-dropdown'}
              required
              disabled={!localData.movementType}
              smallValue={true}
              dataTestId="shipTo-dropdown"
            />
          </div>
        </div>
      </div>
    </StepWrapper>
  );
}
